/**
 * Preguntas de quiz y desafíos por capítulo del tutorial.
 */

export interface QuizQuestion {
  question: string;
  options: string[];
  correctIndex: number;
  explanation: string;
}

export interface QuizSet {
  title: string;
  questions: QuizQuestion[];
}

export interface ChallengeData {
  title: string;
  difficulty: 'facil' | 'medio' | 'avanzado';
  question: string;
  hint?: string;
  answer: string;
}

// ── Quizzes por capítulo ─────────────────────────────────────────

export const QUIZ_CONFIGURACION: QuizSet = {
  title: 'Configuración',
  questions: [
    {
      question: '¿Qué rol puede configurar modelos de pricing?',
      options: ['SALESPERSON', 'APPROVER', 'COMPANY_ADMIN', 'Cualquiera'],
      correctIndex: 2,
      explanation:
        'Solo el COMPANY_ADMIN (como Carlos) puede crear y editar modelos de pricing y datos maestros.',
    },
    {
      question: '¿Qué tipo de variable calcula su valor automáticamente?',
      options: ['NUMBER', 'CURRENCY', 'PERCENTAGE', 'CALCULATED'],
      correctIndex: 3,
      explanation:
        'Las variables CALCULATED usan fórmulas que referencian otras variables del modelo.',
    },
  ],
};

export const QUIZ_CALCULANDO: QuizSet = {
  title: 'Calculando Precios',
  questions: [
    {
      question: 'Si el costo base es $4,690 y el margen es 25%, ¿cuál es la utilidad?',
      options: ['$1,172.50', '$1,000.00', '$4,690.00', '$5,862.50'],
      correctIndex: 0,
      explanation:
        'Utilidad = $4,690 × 25% = $1,172.50. El precio final sería $4,690 + $1,172.50 = $5,862.50.',
    },
    {
      question: '¿Qué pasa cuando el margen real está por debajo del margen piso?',
      options: [
        'Se aprueba automáticamente',
        'Se requiere aprobación manual',
        'Se bloquea la cotización',
        'Se ajusta el margen automáticamente',
      ],
      correctIndex: 2,
      explanation:
        'Cuando el margen está por debajo del piso, la cotización se bloquea y no puede enviarse a aprobación sin justificación.',
    },
    {
      question: '¿Qué muestra la cascada de costos?',
      options: [
        'Solo el precio final',
        'El desglose paso a paso desde el costo hasta el precio',
        'Los impuestos aplicables',
        'El historial de precios',
      ],
      correctIndex: 1,
      explanation:
        'La cascada muestra cada componente del precio: licencias → implementación → soporte → utilidad → precio final.',
    },
  ],
};

export const QUIZ_APROBACION: QuizSet = {
  title: 'Aprobación',
  questions: [
    {
      question: '¿Cuáles son las 3 acciones posibles del aprobador?',
      options: [
        'Aprobar, Rechazar, Eliminar',
        'Aprobar, Rechazar, Solicitar Ajuste',
        'Aprobar, Modificar, Reenviar',
        'Aceptar, Negar, Pausar',
      ],
      correctIndex: 1,
      explanation:
        'El aprobador puede Aprobar, Rechazar o Solicitar Ajuste (que devuelve la cotización al vendedor para modificaciones).',
    },
    {
      question: '¿Puede María editar una cotización mientras está pendiente de aprobación?',
      options: ['Sí, siempre', 'No, está bloqueada', 'Solo el precio', 'Solo las notas'],
      correctIndex: 1,
      explanation:
        'Una cotización en estado PENDING_APPROVAL está bloqueada para edición hasta que el aprobador tome una decisión.',
    },
  ],
};

export const QUIZ_FINAL: QuizSet = {
  title: 'Quiz Final',
  questions: [
    {
      question: '¿Cuál es el flujo correcto de una cotización?',
      options: [
        'Aprobar → Cotizar → Configurar',
        'Configurar → Cotizar → Aprobar',
        'Cotizar → Configurar → Aprobar',
        'Configurar → Aprobar → Cotizar',
      ],
      correctIndex: 1,
      explanation:
        'Primero el admin configura modelos (Carlos), luego el vendedor crea la cotización (María) y finalmente el aprobador decide (Juan).',
    },
    {
      question: '¿Qué ventaja tienen las cotizaciones multi-sección?',
      options: [
        'Son más rápidas de crear',
        'Permiten combinar diferentes modelos de pricing en una sola cotización',
        'Tienen mejor margen',
        'No necesitan aprobación',
      ],
      correctIndex: 1,
      explanation:
        'Las multi-secciones permiten, por ejemplo, combinar licencias de software + horas de soporte en una misma cotización.',
    },
    {
      question: '¿Cómo puede la IA ayudar al aprobador?',
      options: [
        'Aprobando automáticamente',
        'Generando score de riesgo y justificaciones detalladas',
        'Eliminando cotizaciones malas',
        'Cambiando el margen',
      ],
      correctIndex: 1,
      explanation:
        'La IA genera análisis objetivos (score de riesgo, justificaciones) que ayudan al aprobador a tomar decisiones informadas.',
    },
  ],
};

// ── Desafíos por capítulo ────────────────────────────────────────

export const CHALLENGE_CONFIGURACION: ChallengeData = {
  title: 'Desafío: Crear un Modelo',
  difficulty: 'medio',
  question:
    'Si necesitas crear un modelo para cotizar "Horas de Consultoría" con tarifa por hora, descuento por volumen y margen, ¿qué variables definirías y cuáles serían calculadas?',
  hint: 'Piensa en: cantidad (horas), tarifa, descuento, subtotal, margen, precio final.',
  answer:
    'Variables de entrada: CANTIDAD (NUMBER), TARIFA_HORA (CURRENCY), DESCUENTO (PERCENTAGE), MARGEN (PERCENTAGE). Variables calculadas: SUBTOTAL = CANTIDAD * TARIFA_HORA * (100 - DESCUENTO) / 100, UTILIDAD = SUBTOTAL * MARGEN / 100, PRECIO_FINAL = SUBTOTAL + UTILIDAD.',
};

export const CHALLENGE_PREPARACION: ChallengeData = {
  title: 'Desafío: Cotización Rápida',
  difficulty: 'facil',
  question:
    '¿Cuál sería el comando natural para pedirle a la IA que cree una cotización de 100 licencias de Adobe Creative Cloud a $55/u para la empresa YPFB?',
  answer:
    '"Crea una cotización para YPFB con 100 licencias de Adobe Creative Cloud a $55 cada una." — La IA interpreta el lenguaje natural y llena todos los campos automáticamente.',
};

export const CHALLENGE_APROBACION: ChallengeData = {
  title: 'Desafío: Decisión de Aprobación',
  difficulty: 'avanzado',
  question:
    'Recibes una cotización con margen del 12% (piso: 15%, objetivo: 25%). El score de riesgo es 68/100 (Alto). Sin embargo, el cliente tiene $500K en revenue histórico y 90% tasa de éxito. ¿Apruebas, rechazas o pides ajuste? ¿Por qué?',
  hint: 'Considera el balance entre riesgo financiero inmediato y valor del cliente a largo plazo.',
  answer:
    'Respuesta sugerida: Solicitar Ajuste. El margen del 12% está 3pp debajo del piso, lo cual es riesgoso. Sin embargo, dado el historial excelente del cliente ($500K revenue, 90% tasa), se podría aprobar condicionado a que se negocie un contrato de mantenimiento adicional que compense el margen reducido, o pedir al vendedor que intente subir el margen al 15% (piso mínimo).',
};
